// Internal imports
import ErrorHelpers from '~/app/errorHelpers.js';
import XlinkAPI from '~/app/api/xlinkAPI';

export const sigTypes = {
  1: 'tpsign',
  2: 'spsign',
  3: 'ppsign',
  4: 'erosign',
  5: 'tpinitial',
  6: 'spinitial',
  7: 'officersign',
  8: 'tpsign2',
  9: 'spsign2',
  A: 'xsign',
};

export const canvasSize = {
  width: 476,
  height: 100,
};

export const canvasOptions = {
  minWidth: 0.7,
  maxWidth: 1,
  penColor: 'rgb(0, 0, 0)',
};

/*
    Convert html canvas image (png image) to 1 bit bitmap.
    Modified from: https://rephrase.net/box/bitmap/
*/

// Create new pixel array based on original canvas
export const newPixelArray = canvas => {
  const context = canvas.getContext('2d');
  const width = canvas.width;
  const height = canvas.height;
  const imageData = context.getImageData(0, 0, width, height);
  const data = imageData.data;

  const pixels = [];
  // Iterate over each pixel
  for (let i = 0; i < data.length; i += 4) {
    // Get the value of each rgba channel
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    // Treat transparent as white
    if (a === 0) {
      pixels.push('ffffff');
      continue;
    }

    // Get average brightness value
    const brightness = (r + g + b) / 3;

    // If pixel is darker than brightness threshold, convert to black, if not then convert to white
    if (brightness < 200) {
      pixels.push('000000');
    } else {
      pixels.push('ffffff');
    }
  }

  const pixarray = [];
  for (let i = height - 1; i > -1; i--) {
    for (let j = 0; j < width; j++) {
      pixarray.push(pixels[i * width + j]);
    }
  }

  return pixarray;
};

// Convert pixel array to monochrome (1 bit) and pack in rows based on width
export const toMonoPixels = (width, height, pixelArray) => {
  const pixels = [];
  const rowsize = Math.ceil(width / 8);
  let rowPadding = 0;

  // Needs to be padded to a multiple of 4 bytes
  if (rowsize % 4 !== 0) {
    rowPadding = 4 - (rowsize % 4);
  }

  let bit = 0;
  for (let i = 0; i < height; ++i) {
    for (let j = 0; j < width; ++j) {
      const rem = j % 8;
      const currentPixel = pixelArray.shift();

      if (parseInt(currentPixel, 16) !== 0) {
        bit = bit | Math.pow(2, 7 - rem);
      }
      if (rem === 7 || j === width - 1) {
        pixels.push(String.fromCharCode(bit));
        bit = 0;
      }
    }
    for (let p = 0; p < rowPadding; ++p) {
      pixels.push('\x00');
    }
  }
  return pixels.join('');
};

// Fixes canvas pixel issues on High res devices
export const scaleCanvasPixelRatio = (
  canvas,
  width = canvasSize.width,
  height = canvasSize.height,
) => {
  const context = canvas.getContext('2d');

  if (typeof window === 'undefined') return;

  const ratio = window.devicePixelRatio || 1;

  if (ratio !== 1) {
    canvas.width = width * ratio;
    canvas.height = height * ratio;

    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
  } else {
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = '';
    canvas.style.height = '';
  }

  context.scale(ratio, ratio);
};

export const toBMPDataURL = async (canvas, padType, originalPadType, funcCallerName) => {
  try {
    const width = canvas.width; // source canvas width
    const height = canvas.height; // source canvas height

    // IMPORTANT:
    // Previous implementation used `padType === 'screen' && ...`, which returns boolean `false`
    // when padType is not 'screen'. That boolean got concatenated into the BMP binary, producing
    // base64 blobs that decode to "...false". We must never allow that again.

    if (padType !== 'screen') {
      // temp for debugging
      await XlinkAPI.logErrorMessage(
        'toBMPDataURL - Incorrect Pad Type',
        `toBMPDataURL - one - Error -> padType: ${padType}, originalPadType: ${originalPadType}, typeof originalPadType: ${typeof originalPadType}, funcCallerName: ${funcCallerName}`,
        0,
      );

      // ErrorHelpers.handleError(
      //   'Signature conversion error',
      //   ErrorHelpers.createSimpleError(
      //     `toBMPDataURL called with unsupported padType "${padType}". Expected "screen".`,
      //   ),
      // );
      // return null;
    }

    const pixelArray = newPixelArray(canvas); // get new pixel array based on alpha channel
    const imgData = toMonoPixels(width, height, pixelArray);

    // Guardrail: never encode invalid data into the BMP payload
    if (typeof imgData !== 'string' || imgData.length === 0) {
      // temp for debugging
      await XlinkAPI.logErrorMessage(
        'toBMPDataURL - Incorrect imgData',
        `toBMPDataURL - two - Error -> imgData: ${imgData}, originalPadType: ${originalPadType}, typeof originalPadType: ${typeof originalPadType}, funcCallerName: ${funcCallerName}`,
        0,
      );

      // ErrorHelpers.handleError(
      //   'Signature conversion error',
      //   ErrorHelpers.createSimpleError('Invalid BMP image data generated from canvas.'),
      // );
      // return null;
    }

    const dataLength = imgData.length; // bitmap size
    const colorTable = ['000000', 'ffffff']; // color palette
    const colorTableLength = colorTable.length;
    const colorTableSize = colorTableLength * 4;
    const fileSize = 54 + colorTableSize + dataLength;
    const pixelOffset = 54 + colorTableSize;

    const data = [
      // DIB Header
      'BM',
      conv(fileSize),
      '\x00\x00\x00\x00',
      conv(pixelOffset),

      // BMP Header
      '\x28\x00\x00\x00',
      conv(width),
      conv(height),
      '\x01\x00',
      conv(1, 2),
      conv(0),
      conv(dataLength),
      '\x13\x0b\x00\x00',
      '\x13\x0b\x00\x00',
      conv(colorTableLength),
      '\x00\x00\x00\x00',
    ];

    for (let i = 0; i < colorTableLength; i++) {
      data.push(conv(parseInt(colorTable[i], 16)));
    }

    data.push(imgData);

    const joined = data.join('');
    return 'data:image/bmp;base64,' + btoa(joined);
  } catch (err) {
    ErrorHelpers.handleError(
      'Signature conversion error',
      ErrorHelpers.isStructuredError(err)
        ? err
        : ErrorHelpers.createSimpleError(
            `Failed to generate BMP signature data: ${err?.message || String(err)}`,
          ),
    );
    return null;
  }

  function conv(val, len = 4) {
    const res = [];
    for (let i = 0; i < len; i++) {
      res.push(String.fromCharCode((val >> (i * 8)) & 0xff));
    }
    return res.join('');
  }
};

const tempCanvasID = 'resize-canvas-temp';

// Helps to fix larger signature issues with high res devices.
export const resizeCanvas = (canvas, width = canvasSize.width, height = canvasSize.height) => {
  const resizedCanvas = document.createElement('canvas');
  const resizedContext = resizedCanvas.getContext('2d');

  resizedCanvas.height = `${height}`;
  resizedCanvas.width = `${width}`;
  resizedCanvas.style.display = 'none';
  resizedCanvas.id = tempCanvasID;

  resizedContext.drawImage(canvas, 0, 0, width, height);

  return resizedCanvas;
};

// Is our device scaled?
export const isCanvasScaled = () => {
  if (typeof window === 'undefined') return false;

  const dpr = window.devicePixelRatio;
  return dpr > 1 || dpr < 1;
};

export const cleanUpCanvas = () => {
  const canvasElement = document.getElementById(tempCanvasID);
  if (canvasElement === null) return;

  document.getElementById(tempCanvasID).remove();
};
